import { Fragment, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { CoinsIcon, InfoIcon, Loader2Icon } from 'lucide-react'
import { FEATURE_TO_COINS } from 'shared/feature-to-coins'
import { Button } from 'ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'ui/card'
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from 'ui/drawer'

type PlanCardProps = {
  coins: number
  onSelect: () => Promise<void>
  price: number
  title: string
  buttonText?: string
  className?: string
  crossedPrice?: number
}

export const PlanCard = ({
  coins,
  price,
  title,
  crossedPrice,
  onSelect: onSelectProp,
  className,
  buttonText,
}: PlanCardProps) => {
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const onSelect = useCallback(() => {
    setIsLoading(true)
    void onSelectProp().finally(() => setIsLoading(false))
  }, [onSelectProp])

  return (
    <Card
      className={clsx(
        'flex w-full max-w-md flex-col justify-between transition duration-300 hover:scale-105 hover:shadow-lg',
        className,
      )}
    >
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2">
          <div className="flex justify-center gap-2 max-md:items-end md:flex-col">
            {!!crossedPrice && (
              <div className="text-4xl font-bold text-gray-500 line-through dark:text-gray-300">{`$${crossedPrice}`}</div>
            )}
            <div className="text-4xl font-bold">{`$${price}`}</div>
            <div className="text-sm text-gray-500 dark:text-gray-300">{t('per month')}</div>
          </div>
        </div>
        <div className="my-4 flex items-center justify-center gap-4">
          <div className="text-3xl">{Intl.NumberFormat(i18n.language).format(coins)}</div>
          <CoinsIcon className="fill-yellow-400 stroke-yellow-600" />
          <Drawer>
            <DrawerTrigger
              render={
                <Button size="icon" variant="ghost">
                  <InfoIcon className="stroke-blue-600" />
                </Button>
              }
            />
            <DrawerContent>
              <div className="mx-auto w-full max-w-sm">
                <DrawerHeader>
                  <DrawerTitle>{t('Features cost')}</DrawerTitle>
                </DrawerHeader>
                <ul className="grid grid-cols-[max-content_auto] gap-x-4 gap-y-2 p-4">
                  {(Object.keys(FEATURE_TO_COINS) as (keyof typeof FEATURE_TO_COINS)[]).map(feature => (
                    <Fragment key={feature}>
                      <div>{t(feature)}</div>
                      <div className="flex items-center gap-2">
                        <span>{FEATURE_TO_COINS[feature]}</span>
                        <CoinsIcon className="fill-yellow-400 stroke-yellow-600" />
                      </div>
                    </Fragment>
                  ))}
                </ul>
                <DrawerFooter className="p-4">
                  <DrawerClose render={<Button variant="outline" />}>{t('Close')}</DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
        <Button className="w-full" disabled={isLoading} onClick={onSelect}>
          {buttonText ?? t('Get Started')}
          {isLoading && <Loader2Icon className="ml-2 size-4 animate-spin" />}
        </Button>
      </CardContent>
    </Card>
  )
}
